import { useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStudyGroupInfo } from "@/hooks/useStudyGroupInfo";
import type {
  MentionCandidate,
  StudyGroupMention,
  User,
} from "@/lib/humhub-api/types";
import {
  createMentionElement,
  generateMentionTextFromCandidate,
  sortMentionCandidatesByMatch,
  studyGroupToMentionCandidate,
  userToMentionCandidate,
} from "@/lib/utils/mentionUtils";

interface MentionPosition {
  top: number;
  left: number;
}

interface MentionInfo {
  node: Node | null;
  position: number;
  atIndex: number;
}

interface UseMentionProps {
  onFetchUsers?: (query: string, context: "post" | "chat") => Promise<User[]>;
  onMentionSelect?: (candidate: MentionCandidate) => string;
  context?: "post" | "chat";
  includeStudyGroup?: boolean;
  scopeKey?: string;
  hasMoreCandidates?: boolean;
  isFetchingMoreCandidates?: boolean;
  onFetchMoreCandidates?: () => Promise<User[]>;
  mentionUsers?: User[];
}

interface UseMentionReturn {
  // State
  mentionQuery: string;
  mentionPosition: MentionPosition | null;
  showMentionSuggestions: boolean;
  selectedMentionIndex: number;
  isLoadingMentionUsers: boolean;
  mentionCandidates: MentionCandidate[];
  filteredMentionCandidates: MentionCandidate[];

  // Handlers
  handleTextInput: (
    selection: Selection | null,
    editorRef: React.RefObject<HTMLDivElement | null>,
  ) => void;
  handleKeyDown: (e: React.KeyboardEvent) => boolean;
  insertMention: (
    candidate: MentionCandidate,
    editorRef: React.RefObject<HTMLDivElement | null>,
  ) => void;
  hideMentionSuggestions: () => void;
  fetchMoreMentionUsers: () => Promise<void>;

  // Refs
  mentionSuggestionsRef: React.RefObject<HTMLDivElement | null>;
}

export const useMention = ({
  onFetchUsers,
  onMentionSelect = generateMentionTextFromCandidate,
  context = "post",
  includeStudyGroup = true,
  scopeKey = "",
  hasMoreCandidates = false,
  isFetchingMoreCandidates = false,
  onFetchMoreCandidates,
  mentionUsers,
}: UseMentionProps): UseMentionReturn => {
  const positionCursorAfterMention = useCallback(
    (mentionElement: HTMLElement, selection: Selection | null) => {
      if (!selection) return;

      const newRange = document.createRange();
      const spaceNode = mentionElement.nextSibling;

      if (spaceNode && spaceNode.nodeType === Node.TEXT_NODE) {
        const spaceLength = spaceNode.textContent?.length ?? 0;
        newRange.setStart(spaceNode, spaceLength);
      } else {
        newRange.setStartAfter(mentionElement);
      }

      newRange.collapse(true);

      selection.removeAllRanges();
      selection.addRange(newRange);
    },
    [],
  );

  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionPosition, setMentionPosition] =
    useState<MentionPosition | null>(null);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [isLoadingMentionUsers, setIsLoadingMentionUsers] = useState(false);
  const [mentionUserPages, setMentionUserPages] = useState<User[][]>([]);
  const [lastMentionInfo, setLastMentionInfo] = useState<MentionInfo | null>(
    null,
  );

  const mentionSuggestionsRef = useRef<HTMLDivElement | null>(null);
  const fetchRequestIdRef = useRef(0);
  const scopeKeyRef = useRef(scopeKey);

  const reconcileMentionUserPages = useCallback(
    (pages: User[][], accumulatedUsers: User[]) => {
      if (accumulatedUsers.length === 0) return [];
      if (pages.length === 0) return [accumulatedUsers];

      const reconciledPages: User[][] = [];
      let offset = 0;
      for (const page of pages) {
        if (offset >= accumulatedUsers.length) break;
        reconciledPages.push(
          accumulatedUsers.slice(offset, offset + page.length),
        );
        offset += page.length;
      }
      if (offset < accumulatedUsers.length) {
        reconciledPages.push(accumulatedUsers.slice(offset));
      }
      return reconciledPages;
    },
    [],
  );

  useEffect(() => {
    scopeKeyRef.current = scopeKey;
    fetchRequestIdRef.current += 1;
    setMentionQuery("");
    setMentionPosition(null);
    setShowMentionSuggestions(false);
    setSelectedMentionIndex(0);
    setIsLoadingMentionUsers(false);
    setMentionUserPages([]);
    setLastMentionInfo(null);
  }, [scopeKey]);

  useEffect(() => {
    if (!mentionUsers) return;
    setMentionUserPages((pages) =>
      reconcileMentionUserPages(pages, mentionUsers),
    );
  }, [mentionUsers, reconcileMentionUserPages]);

  // 現在のルートからスタディグループIDを特定
  const routerMatches = useRouterState({
    select: (state) => state.matches,
  });
  const studyGroupIdParam =
    routerMatches
      .map(
        (m) =>
          (m.params as Record<string, string | undefined>)?.["study-group-id"],
      )
      .find(Boolean) ?? null;
  const studyGroupId = studyGroupIdParam ? Number(studyGroupIdParam) : null;
  const { studyGroupInfo } = useStudyGroupInfo(studyGroupId);

  // スタディグループをMentionCandidateに変換
  const studyGroupCandidate = useMemo((): MentionCandidate | null => {
    // guidが必須 - フォールバックは使用しない
    if (
      !includeStudyGroup ||
      !studyGroupInfo?.guid ||
      studyGroupInfo.guid.trim() === "" ||
      !studyGroupInfo.name
    ) {
      console.warn("Study group mention skipped: missing GUID or name", {
        includeStudyGroup,
        guid: studyGroupInfo?.guid,
        name: studyGroupInfo?.name,
      });
      return null;
    }

    const studyGroupMention: StudyGroupMention = {
      guid: studyGroupInfo.guid, // 常に正しいGUIDを使用
      name: studyGroupInfo.name,
      imageUrl: studyGroupInfo.image_url || undefined,
    };

    return studyGroupToMentionCandidate(studyGroupMention, mentionQuery);
  }, [includeStudyGroup, studyGroupInfo, mentionQuery]);

  // ユーザーをMentionCandidateに変換
  const userCandidatePages = useMemo((): MentionCandidate[][] => {
    return mentionUserPages.map((page) =>
      page.map((user) => userToMentionCandidate(user, mentionQuery)),
    );
  }, [mentionUserPages, mentionQuery]);

  // 全メンション候補を統合してソート
  const mentionCandidates = useMemo((): MentionCandidate[] => {
    const allCandidates: MentionCandidate[] = userCandidatePages.flat();

    if (studyGroupCandidate) {
      allCandidates.push(studyGroupCandidate);
    }

    return allCandidates;
  }, [userCandidatePages, studyGroupCandidate]);

  // フィルタリングとソート済みの候補リスト
  const filteredMentionCandidates = useMemo((): MentionCandidate[] => {
    const userCandidates = userCandidatePages.flatMap((page) =>
      sortMentionCandidatesByMatch(
        page.filter((candidate) => (candidate.matchScore || 0) > 0),
      ),
    );
    const groupCandidates =
      studyGroupCandidate && (studyGroupCandidate.matchScore || 0) > 0
        ? [studyGroupCandidate]
        : [];

    return [...groupCandidates, ...userCandidates];
  }, [studyGroupCandidate, userCandidatePages]);

  useEffect(() => {
    const lastSelectableIndex =
      filteredMentionCandidates.length - 1 + (hasMoreCandidates ? 1 : 0);
    setSelectedMentionIndex((currentIndex) =>
      Math.max(0, Math.min(currentIndex, lastSelectableIndex)),
    );
  }, [filteredMentionCandidates.length, hasMoreCandidates]);

  const fetchMoreMentionUsers = useCallback(async () => {
    if (!onFetchMoreCandidates || isFetchingMoreCandidates) return;
    const requestId = fetchRequestIdRef.current;
    const requestScopeKey = scopeKeyRef.current;
    const users = await onFetchMoreCandidates();
    if (
      requestId !== fetchRequestIdRef.current ||
      requestScopeKey !== scopeKeyRef.current
    ) {
      return;
    }
    setMentionUserPages((pages) => reconcileMentionUserPages(pages, users));
  }, [
    isFetchingMoreCandidates,
    onFetchMoreCandidates,
    reconcileMentionUserPages,
  ]);

  // Fetch users when mention is triggered
  const fetchMentionUsers = useCallback(
    async (query = "") => {
      if (!onFetchUsers) return;

      const requestId = fetchRequestIdRef.current + 1;
      fetchRequestIdRef.current = requestId;
      const requestScopeKey = scopeKeyRef.current;

      setIsLoadingMentionUsers(true);
      setMentionQuery(query);

      try {
        const users = await onFetchUsers(query, context);
        if (
          requestId !== fetchRequestIdRef.current ||
          requestScopeKey !== scopeKeyRef.current
        ) {
          return;
        }
        setMentionUserPages([users]);
      } catch (error) {
        if (
          requestId !== fetchRequestIdRef.current ||
          requestScopeKey !== scopeKeyRef.current
        ) {
          return;
        }
        console.error("Failed to fetch mention users:", error);
        setMentionUserPages([]);
      } finally {
        if (
          requestId === fetchRequestIdRef.current &&
          requestScopeKey === scopeKeyRef.current
        ) {
          setIsLoadingMentionUsers(false);
        }
      }
    },
    [onFetchUsers, context],
  );

  // Calculate mention position
  const calculateMentionPosition = useCallback(
    (
      range: Range,
      editorRef: React.RefObject<HTMLDivElement | null>,
    ): MentionPosition | null => {
      if (!editorRef.current) return null;

      try {
        const cursorRect = range.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();

        const suggestionsHeight = 280;
        const suggestionsWidth = 300;

        let top: number;
        let left: number;

        const spaceAbove = cursorRect.top;
        const spaceBelow = window.innerHeight - cursorRect.bottom;

        if (spaceAbove >= suggestionsHeight + 16) {
          top = cursorRect.top - suggestionsHeight - 8;
        } else if (spaceBelow >= suggestionsHeight + 16) {
          top = cursorRect.bottom + 8;
        } else {
          if (editorRect.top >= suggestionsHeight + 16) {
            top = editorRect.top - suggestionsHeight - 8;
          } else {
            top = editorRect.bottom + 8;
          }
        }

        left = cursorRect.left;

        if (left + suggestionsWidth > window.innerWidth - 16) {
          left = window.innerWidth - suggestionsWidth - 16;
        }

        if (left < 16) {
          left = 16;
        }

        if (top < 16) {
          top = 16;
        }

        if (top + suggestionsHeight > window.innerHeight - 16) {
          top = window.innerHeight - suggestionsHeight - 16;
        }

        return {
          top: Math.round(top),
          left: Math.round(left),
        };
      } catch (error) {
        console.error("Failed to calculate mention position:", error);

        if (editorRef.current) {
          const editorRect = editorRef.current.getBoundingClientRect();
          return {
            top: Math.max(16, editorRect.top - 290),
            left: Math.max(16, editorRect.left + 50),
          };
        }

        return {
          top: 100,
          left: 100,
        };
      }
    },
    [],
  );

  // Handle text input for mention detection
  const handleTextInput = useCallback(
    (
      selection: Selection | null,
      editorRef: React.RefObject<HTMLDivElement | null>,
    ) => {
      // selectionの型安全性チェック
      if (
        !selection ||
        typeof selection.getRangeAt !== "function" ||
        selection.rangeCount === 0
      ) {
        setShowMentionSuggestions(false);
        return;
      }

      let range: Range;
      try {
        range = selection.getRangeAt(0);
      } catch (error) {
        console.error("Failed to get range from selection:", error);
        setShowMentionSuggestions(false);
        return;
      }

      const currentNode = range.startContainer;

      if (currentNode.nodeType !== Node.TEXT_NODE) {
        setShowMentionSuggestions(false);
        return;
      }

      const text = currentNode.textContent || "";
      const position = range.startOffset;
      const beforeCursor = text.substring(0, position);
      const lastAtIndex = beforeCursor.lastIndexOf("@");

      if (lastAtIndex === -1) {
        setShowMentionSuggestions(false);
        return;
      }

      const charBeforeAt =
        lastAtIndex === 0 ? " " : beforeCursor[lastAtIndex - 1];
      if (charBeforeAt !== " " && charBeforeAt !== "\n") {
        setShowMentionSuggestions(false);
        return;
      }

      const query = beforeCursor.substring(lastAtIndex + 1);

      if (query.includes(" ")) {
        setShowMentionSuggestions(false);
        return;
      }

      setMentionQuery(query);

      setLastMentionInfo({
        node: currentNode,
        position: position,
        atIndex: lastAtIndex,
      });

      const newPosition = calculateMentionPosition(range, editorRef);
      if (newPosition) {
        setMentionPosition(newPosition);
        fetchMentionUsers(query);
        setShowMentionSuggestions(true);
        setSelectedMentionIndex(0);
      }
    },
    [calculateMentionPosition, fetchMentionUsers],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!showMentionSuggestions) return false;

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          setSelectedMentionIndex((prev) => Math.max(0, prev - 1));
          return true;

        case "ArrowDown":
          e.preventDefault();
          setSelectedMentionIndex((prev) =>
            Math.min(
              filteredMentionCandidates.length -
                1 +
                (hasMoreCandidates ? 1 : 0),
              prev + 1,
            ),
          );
          return true;

        case "Enter":
          e.preventDefault();
          if (
            hasMoreCandidates &&
            selectedMentionIndex === filteredMentionCandidates.length
          ) {
            void fetchMoreMentionUsers();
          }
          if (filteredMentionCandidates.length > 0) {
            return true;
          }
          return false;

        case "Escape":
          setShowMentionSuggestions(false);
          return true;

        default:
          return false;
      }
    },
    [
      fetchMoreMentionUsers,
      filteredMentionCandidates.length,
      hasMoreCandidates,
      selectedMentionIndex,
      showMentionSuggestions,
    ],
  );

  // Insert mention into text
  const insertMention = useCallback(
    (
      candidate: MentionCandidate,
      editorRef: React.RefObject<HTMLDivElement | null>,
    ) => {
      if (!editorRef.current) return;

      const mentionText = onMentionSelect(candidate);
      const displayName = candidate.displayName;

      editorRef.current.focus();

      // 優先的にlastMentionInfoを使用（クリック選択の場合）
      if (
        lastMentionInfo?.node &&
        lastMentionInfo.node.nodeType === Node.TEXT_NODE
      ) {
        const text = lastMentionInfo.node.textContent || "";
        const beforeText = text.substring(0, lastMentionInfo.atIndex);
        const afterText = text.substring(lastMentionInfo.position);

        // Create mention element
        const mentionElement = createMentionElement(`@${displayName}`);
        mentionElement.setAttribute("data-mention", mentionText);

        // スタディグループの場合は異なるスタイルを適用
        if (candidate.type === "study_group") {
          mentionElement.style.cssText += `
            background-color: var(--rw-mention-bg) !important;
            padding: 1px 4px !important;
            border-radius: 3px !important;
            font-weight: 500 !important;
          `;
        }

        // Replace the text node
        const parentNode = lastMentionInfo.node.parentNode;
        if (parentNode) {
          // Create document fragment
          const fragment = document.createDocumentFragment();

          if (beforeText) {
            fragment.appendChild(document.createTextNode(beforeText));
          }

          fragment.appendChild(mentionElement);
          fragment.appendChild(document.createTextNode(" "));

          if (afterText) {
            fragment.appendChild(document.createTextNode(afterText));
          }

          // Replace the text node
          parentNode.replaceChild(fragment, lastMentionInfo.node);

          // Set cursor after mention
          positionCursorAfterMention(mentionElement, window.getSelection());

          setLastMentionInfo(null);
          setShowMentionSuggestions(false);
          return;
        }
      }

      // フォールバック: 現在の選択を使用（キーボード選択など）
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const currentNode = range.startContainer;

        if (currentNode.nodeType === Node.TEXT_NODE) {
          const text = currentNode.textContent || "";
          const position = range.startOffset;
          const beforeCursor = text.substring(0, position);
          const lastAtIndex = beforeCursor.lastIndexOf("@");

          if (lastAtIndex !== -1) {
            const beforeText = text.substring(0, lastAtIndex);
            const afterText = text.substring(position);

            // Create mention element
            const mentionElement = createMentionElement(`@${displayName}`);
            mentionElement.setAttribute("data-mention", mentionText);

            // スタディグループの場合は異なるスタイルを適用
            if (candidate.type === "study_group") {
              mentionElement.style.cssText += `
                background-color: var(--rw-mention-bg) !important;
                padding: 1px 4px !important;
                border-radius: 3px !important;
                font-weight: 500 !important;
              `;
            }

            // Replace the text node
            const parentNode = currentNode.parentNode;
            if (parentNode) {
              // Create document fragment
              const fragment = document.createDocumentFragment();

              if (beforeText) {
                fragment.appendChild(document.createTextNode(beforeText));
              }

              fragment.appendChild(mentionElement);
              fragment.appendChild(document.createTextNode(" "));

              if (afterText) {
                fragment.appendChild(document.createTextNode(afterText));
              }

              // Replace the text node
              parentNode.replaceChild(fragment, currentNode);

              // Set cursor after mention
              positionCursorAfterMention(mentionElement, selection);
            }
          }
        }
      }

      setShowMentionSuggestions(false);
    },
    [onMentionSelect, lastMentionInfo, positionCursorAfterMention],
  );

  // Hide mention suggestions
  const hideMentionSuggestions = useCallback(() => {
    setShowMentionSuggestions(false);
  }, []);

  return {
    // State
    mentionQuery,
    mentionPosition,
    showMentionSuggestions,
    selectedMentionIndex,
    isLoadingMentionUsers,
    mentionCandidates,
    filteredMentionCandidates,

    // Handlers
    handleTextInput,
    handleKeyDown,
    insertMention,
    hideMentionSuggestions,
    fetchMoreMentionUsers,

    // Refs
    mentionSuggestionsRef,
  };
};
